import { useEffect, useRef, useState } from 'react';

/**
 * Holds `loading` true for at least `minMs` after it first turned true,
 * so skeleton/loading UI is actually perceivable on fast networks.
 *
 * Without this, a 150ms fetch on a fast connection makes the skeleton
 * flash for one frame and the page appears to "jump" — users perceive
 * it as broken or as if nothing happened.
 */
export function useMinDelayed(loading: boolean, minMs = 450): boolean {
  const [held, setHeld] = useState(loading);
  const startedAt = useRef<number | null>(loading ? Date.now() : null);

  useEffect(() => {
    if (loading) {
      startedAt.current = Date.now();
      setHeld(true);
      return;
    }
    const elapsed = startedAt.current ? Date.now() - startedAt.current : minMs;
    const remaining = Math.max(0, minMs - elapsed);
    if (remaining === 0) {
      setHeld(false);
      return;
    }
    const t = setTimeout(() => setHeld(false), remaining);
    return () => clearTimeout(t);
  }, [loading, minMs]);

  return held;
}
